import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import NavTabItem from './components/NavTabItem';
import NavPreviewCard from './components/NavPreviewCard';
import MobileBottomNav from '../../components/ui/MobileBottomNav';

const TABS = [
  {
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/mobile-dashboard',
    description: 'Key metrics, active projects overview, and resource status at a glance.',
  },
  {
    label: 'Bids',
    icon: 'FileText',
    path: '/mobile-bid-management',
    description: 'Browse, filter, and manage bids. View details and convert accepted bids to projects.',
  },
  {
    label: 'Customers',
    icon: 'Users',
    path: '/mobile-customer-management',
    description: 'Search customers, view contact details, and quickly create bids or projects.',
  },
  {
    label: 'Install',
    icon: 'Download',
    path: '/pwa-install-instructions',
    description: 'Install the app on your iOS or Android device for the best mobile experience.',
  },
];

const FEATURES = [
  { icon: 'Fingerprint', label: '44px touch targets', desc: 'Meets accessibility tap-target guidelines' },
  { icon: 'Zap', label: 'Instant navigation', desc: 'No page reload — seamless screen transitions' },
  { icon: 'Eye', label: 'Active indicators', desc: 'Top bar + color highlight on current tab' },
  { icon: 'Smartphone', label: 'Platform-aware', desc: 'Adapts to iOS safe area and Android chrome' },
];

const MobileBottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [previewActive, setPreviewActive] = useState(0);

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"
            >
              <Icon name="ArrowLeft" size={18} className="text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-foreground">Bottom Navigation</h1>
              <p className="text-xs text-muted-foreground">Mobile PWA tab bar</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-5 pb-28 space-y-6">

          {/* Live Preview */}
          <section>
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Live Preview</h2>
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              {/* Mock screen area */}
              <div className="bg-muted/40 h-28 flex items-center justify-center">
                <div className="text-center">
                  <Icon name={TABS?.[previewActive]?.icon} size={28} className="text-muted-foreground mx-auto mb-1" />
                  <p className="text-xs text-muted-foreground font-medium">{TABS?.[previewActive]?.label} screen</p>
                </div>
              </div>
              {/* Nav bar preview */}
              <div className="bg-card border-t border-border">
                <div className="flex items-stretch">
                  {TABS?.map((tab, idx) => (
                    <NavTabItem
                      key={tab?.path}
                      tab={tab}
                      isActive={previewActive === idx}
                      onClick={() => setPreviewActive(idx)}
                      badge={undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">Tap tabs above to preview active states</p>
          </section>

          {/* Tab Destinations */}
          <section>
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Tab Destinations</h2>
            <div className="space-y-2.5">
              {TABS?.map((tab, idx) => (
                <NavPreviewCard
                  key={tab?.path}
                  tab={tab}
                  isActive={location?.pathname === tab?.path}
                />
              ))}
            </div>
          </section>

          {/* Features */}
          <section>
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Design Features</h2>
            <div className="grid grid-cols-2 gap-2.5">
              {FEATURES?.map((f, idx) => (
                <div key={idx} className="bg-card border border-border rounded-2xl p-3.5">
                  <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center mb-2.5">
                    <Icon name={f?.icon} size={18} className="text-accent" />
                  </div>
                  <p className="text-xs font-semibold text-foreground mb-0.5">{f?.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{f?.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Quick launch */}
          <section>
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Quick Launch</h2>
            <div className="grid grid-cols-2 gap-2.5">
              {TABS?.map((tab) => (
                <button
                  key={tab?.path}
                  onClick={() => navigate(tab?.path)}
                  className={`flex items-center gap-2.5 bg-card border border-border rounded-2xl px-3.5 py-3 active:scale-95 transition-transform ${
                    location?.pathname === tab?.path ? 'border-accent/40' : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    location?.pathname === tab?.path ? 'bg-accent/15' : 'bg-muted'
                  }`}>
                    <Icon
                      name={tab?.icon}
                      size={16}
                      className={location?.pathname === tab?.path ? 'text-accent' : 'text-muted-foreground'}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground truncate">{tab?.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Config link */}
          <section>
            <button
              onClick={() => navigate('/mobile-app-configuration')}
              className="w-full flex items-center justify-between bg-card border border-border rounded-2xl px-4 py-3.5 active:scale-[0.99] transition-transform"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                  <Icon name="Settings" size={18} className="text-muted-foreground" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">Mobile App Configuration</p>
                  <p className="text-xs text-muted-foreground">Manage screen visibility per platform</p>
                </div>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </button>
          </section>
        </div>

        {/* Bottom Nav */}
        <MobileBottomNav />
      </div>
    </MainLayout>
  );
};

export default MobileBottomNavigation;
